<script setup>
import { railsApi } from '@/utils/railsApi'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t, te } = useI18n()

const loading = ref(true)
const error = ref('')
const trainerCount = ref(0)
const recentLogs = ref([])

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const data = await railsApi('/api/v1/admin/dashboard')

    trainerCount.value = data.trainer_count
    recentLogs.value = data.recent_audit_logs || []
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.dashboard.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const formatWhen = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleString('nl-NL')
}

const actionLabel = action => {
  const key = `admin.actions.${action}`

  return te(key) ? t(key) : action
}
</script>

<template>
  <div>
    <h1 class="text-h4 mb-4">
      {{ $t('admin.dashboard.title') }}
    </h1>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>

    <VRow>
      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardText>
            <div class="text-subtitle-2 text-medium-emphasis mb-1">
              {{ $t('admin.dashboard.trainerCount') }}
            </div>
            <div class="text-h3">
              <VProgressCircular
                v-if="loading"
                indeterminate
                size="28"
              />
              <span v-else>{{ trainerCount }}</span>
            </div>
            <VBtn
              class="mt-4"
              color="primary"
              :to="{ name: 'admin-trainers' }"
            >
              {{ $t('admin.dashboard.searchTrainers') }}
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VCard class="mt-6">
      <VCardItem>
        <VCardTitle>{{ $t('admin.dashboard.recentAudit') }}</VCardTitle>
      </VCardItem>
      <VCardText>
        <p
          v-if="!loading && !recentLogs.length"
          class="text-medium-emphasis mb-0"
        >
          {{ $t('admin.dashboard.emptyAudit') }}
        </p>
        <VTable v-else-if="recentLogs.length">
          <thead>
            <tr>
              <th scope="col">
                {{ $t('admin.columns.when') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.who') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.action') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.target') }}
              </th>
              <th scope="col">
                {{ $t('admin.columns.reason') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="log in recentLogs"
              :key="log.id"
            >
              <td>{{ formatWhen(log.created_at) }}</td>
              <td>{{ log.platform_admin?.email }}</td>
              <td>{{ actionLabel(log.action) }}</td>
              <td>{{ log.auditable_label }}</td>
              <td>{{ log.reason }}</td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>
  </div>
</template>
